import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { ContentType } from "@/types/content";
import { useMediaChapters, useChaptersStatus, type MediaChapter } from "./useMediaChapters";
import { useDocumentChapters, useDocumentChaptersStatus, type DocumentChapter } from "./useDocumentChapters";

// Unified chapter type that works for both media and documents
export interface UnifiedChapter {
  id: string;
  contentId: string;
  title: string;
  summary: string | null;
  // For media: start/end in seconds
  // For documents: start/end in pages
  startPosition: number;
  endPosition: number;
  positionType: 'seconds' | 'pages';
  orderIndex: number;
  createdAt: string;
}

// Helper to determine if content type is media
export function isMediaContentType(type: ContentType): boolean {
  return ['youtube', 'audio', 'video', 'recording'].includes(type);
}

// Helper to determine if content type is document
export function isDocumentContentType(type: ContentType): boolean {
  return ['pdf', 'doc', 'ppt', 'web', 'text'].includes(type);
}

// Convert media chapter to unified format
function mediaChapterToUnified(chapter: MediaChapter, index: number): UnifiedChapter {
  return {
    id: chapter.id,
    contentId: chapter.content_id,
    title: chapter.title,
    summary: chapter.summary,
    startPosition: chapter.start_seconds,
    endPosition: chapter.end_seconds,
    positionType: 'seconds',
    orderIndex: index,
    createdAt: chapter.created_at,
  };
}

// Convert document chapter to unified format
function documentChapterToUnified(chapter: DocumentChapter): UnifiedChapter {
  return {
    id: chapter.id,
    contentId: chapter.content_id,
    title: chapter.title,
    summary: chapter.summary,
    startPosition: chapter.start_page,
    endPosition: chapter.end_page,
    positionType: 'pages',
    orderIndex: chapter.order_index,
    createdAt: chapter.created_at,
  };
}

/**
 * Unified hook that fetches chapters for both media and document content types.
 * 
 * @param contentId - The content ID
 * @param contentType - The type of content (pdf, youtube, audio, etc.)
 * @returns Query result with unified chapter format
 */
export function useContentChapters(contentId: string | undefined, contentType: ContentType | undefined) {
  const isMedia = contentType ? isMediaContentType(contentType) : false;
  const isDocument = contentType ? isDocumentContentType(contentType) : false;

  // Use appropriate hook based on content type
  const mediaChaptersQuery = useMediaChapters(isMedia ? contentId : undefined);
  const documentChaptersQuery = useDocumentChapters(isDocument ? contentId : undefined);

  // Select the appropriate query based on content type
  const activeQuery = isMedia ? mediaChaptersQuery : documentChaptersQuery;

  // Transform data to unified format
  const unifiedChapters: UnifiedChapter[] | undefined = activeQuery.data
    ? isMedia
      ? (activeQuery.data as MediaChapter[]).map((ch, idx) => mediaChapterToUnified(ch, idx))
      : (activeQuery.data as DocumentChapter[]).map(ch => documentChapterToUnified(ch))
    : undefined;

  return {
    ...activeQuery,
    data: unifiedChapters,
    isMedia,
    isDocument,
  };
}

/**
 * Unified hook for chapter generation status.
 */
export function useContentChaptersStatus(contentId: string | undefined, contentType: ContentType | undefined) {
  const isMedia = contentType ? isMediaContentType(contentType) : false;
  const isDocument = contentType ? isDocumentContentType(contentType) : false;

  const mediaStatusQuery = useChaptersStatus(isMedia ? contentId : undefined);
  const documentStatusQuery = useDocumentChaptersStatus(isDocument ? contentId : undefined);

  return isMedia ? mediaStatusQuery : documentStatusQuery;
}

/**
 * Get topics from chapters (for flashcard/quiz generation).
 * Returns chapter titles as topics.
 */
export function useContentTopicsFromChapters(contentId: string | undefined, contentType: ContentType | undefined) {
  const { data: chapters, isLoading, error } = useContentChapters(contentId, contentType);

  const topics = chapters?.map(ch => ch.title) || [];

  return {
    topics,
    isLoading,
    error,
    hasChapters: (chapters?.length || 0) > 0,
  };
}